import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Star,
    ShoppingBag,
    BookOpen,
    Disc,
    Tablet,
    Library,
    MapPin,
    ExternalLink,
    ThumbsUp,
    ThumbsDown,
    Check,
    ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAvailableStoresForBook } from "@/lib/affiliates";
import { useCountry } from "@/contexts/CountryContext";
import { useReading } from "@/contexts/ReadingContext";
import UsedListingPill from "@/components/UsedListingPill";
import LocalBookshopCTA from "@/components/LocalBookshopCTA";

const ICONS = { ShoppingBag, BookOpen, Disc, Tablet, Library, MapPin };

function Stars({ rating }) {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    const nodes = [];
    for (let i = 0; i < 5; i++) {
        const filled = i < full || (i === full && half);
        nodes.push(
            <Star
                key={i}
                size={13}
                strokeWidth={1.5}
                className={filled ? "fill-[var(--star)] text-[var(--star)]" : "text-[var(--border-strong)]"}
            />
        );
    }
    return (
        <div className="flex items-center gap-0.5">
            {nodes}
            <span className="ml-2 text-xs text-[var(--ink-muted)] font-mono-num">
                {rating.toFixed(1)}
            </span>
        </div>
    );
}

export default function BookCard({ book, index = 0, recommendationId, readonly = false }) {
    const [coverFailed, setCoverFailed] = useState(false);
    const { country } = useCountry();
    const stores = getAvailableStoresForBook(book, country);
    const reading = useReading();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        setCoverFailed(false);
    }, [book.cover_url]);

    const coverUrl = book.cover_url;
    const sentiment = reading.getSentiment(book);
    const isRead = reading.isRead(book);

    return (
        <article
            data-testid={`book-card-${index}`}
            className={`flex flex-col h-full bg-[var(--bg-elevated)] border p-6 transition-all duration-300 hover:-translate-y-1 animate-fade-up ${
                isRead ? "border-[var(--accent)]/50" : "border-[var(--border)]"
            }`}
            style={{ animationDelay: `${index * 80}ms` }}
        >
            <div className="relative w-full aspect-[2/3] bg-[var(--bg-strong)] border border-[var(--border)] mb-5 overflow-hidden flex items-center justify-center">
                {coverUrl && !coverFailed ? (
                    <img
                        src={coverUrl}
                        alt={book.title}
                        onError={() => setCoverFailed(true)}
                        onLoad={(e) => {
                            // Frontend safety net for Google Books' "image
                            // not available" placeholder. The canonical
                            // placeholder is 575×750 px (portrait, ratio
                            // 0.77 — same as a real book), so the simple
                            // "wider than tall" check doesn't catch it.
                            // Match by exact-size signature instead. The
                            // square-shaped variants (older) are still
                            // caught by the width≥height fallback.
                            const img = e.currentTarget;
                            const w = img.naturalWidth;
                            const h = img.naturalHeight;
                            if (w === 0 || h === 0) return;
                            const isGbPlaceholder = w === 575 && h === 750;
                            const isSquareishPlaceholder = w >= h;
                            if (isGbPlaceholder || isSquareishPlaceholder) {
                                setCoverFailed(true);
                            }
                        }}
                        className={`w-full h-full object-cover transition-opacity ${isRead ? "opacity-60" : ""}`}
                        loading="lazy"
                    />
                ) : (
                    <div
                        data-testid={`book-cover-fallback-${index}`}
                        className="relative w-full h-full flex flex-col items-center justify-center text-center px-5 py-6"
                        style={{
                            backgroundImage:
                                "linear-gradient(135deg, var(--bg-strong) 0%, var(--bg-elevated) 100%)",
                        }}
                    >
                        {/* Decorative book-spine line on the left so the
                            fallback reads as a stylised cover, not "missing
                            asset". Matches the cinematic-dark theme. */}
                        <span className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--accent)]/40" />
                        <span className="absolute right-3 top-3 text-[9px] uppercase tracking-[0.25em] text-[var(--ink-dim)]/60 font-mono-num">
                            {book.year || "—"}
                        </span>

                        <BookOpen
                            size={32}
                            strokeWidth={1}
                            className="text-[var(--accent)]/70 mb-4"
                        />
                        <span
                            className="font-display text-xl leading-tight text-[var(--ink)] line-clamp-4"
                            style={{ fontVariationSettings: '"wght" 600, "wdth" 95, "opsz" 32' }}
                        >
                            {book.title}
                        </span>
                        <span className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)] line-clamp-2">
                            {book.author}
                        </span>
                        <span className="absolute bottom-3 inset-x-0 text-center text-[9px] uppercase tracking-[0.3em] text-[var(--ink-dim)]/50">
                            {t("book_card.no_cover_label", { defaultValue: "Sin portada" })}
                        </span>
                    </div>
                )}
                {isRead && (
                    <div
                        data-testid={`read-badge-${index}`}
                        className="absolute top-2 right-2 bg-[var(--accent)] text-[var(--bg)] text-[10px] uppercase tracking-widest px-2.5 py-1 flex items-center gap-1"
                    >
                        <Check size={11} strokeWidth={2} />
                        <span>{t("book_card.read_badge")}</span>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between gap-2 text-xs uppercase tracking-widest text-[var(--ink-dim)] mb-3">
                <span>{book.genre || t("book_card.fiction")}</span>
                {book.year && <span>{book.year}</span>}
            </div>

            <h3
                data-testid={`book-title-${index}`}
                className="font-display text-2xl leading-tight text-[var(--ink)] mb-1"
                style={{ fontVariationSettings: '"wght" 600, "wdth" 95, "opsz" 32' }}
            >
                {book.title}
            </h3>
            <p className="text-sm text-[var(--ink-muted)] mb-3">{book.author}</p>

            <Stars rating={book.rating ?? 4.0} />
            {(book.rating_total_reviews || book.rating_count_estimate) && (
                <p className="text-xs text-[var(--ink-dim)] mt-1 italic">
                    {book.rating_total_reviews
                        ? t("book_card.rating_total", {
                              count: book.rating_total_reviews,
                              n: book.rating_total_reviews >= 1000
                                  ? `${Math.round(book.rating_total_reviews / 1000)}k`
                                  : book.rating_total_reviews,
                              sources: book.rating_sources?.map((s) => s.source).join(" + ") || "",
                          })
                        : book.rating_count_estimate}
                    {" · "}
                    <Link
                        to="/valoraciones"
                        className="text-[var(--accent)] hover:underline"
                        data-testid={`rating-methodology-link-${index}`}
                    >
                        {t("book_card.how_we_rate")}
                    </Link>
                </p>
            )}

            {book.synopsis && (
                <CollapsibleSynopsis text={book.synopsis} index={index} />
            )}

            {book.why_match && (
                <div className="mt-4 pt-4 border-t border-[var(--border)]">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] mb-1.5 font-medium">
                        {t("book_card.why")}
                    </p>
                    <p className="text-sm text-[var(--ink)] leading-relaxed italic">
                        {book.why_match}
                    </p>
                </div>
            )}

            {!readonly && (
                <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-testid={`like-btn-${index}`}
                            onClick={() => reading.setSentiment({ ...book, recommendation_id: recommendationId }, "like")}
                            aria-pressed={sentiment === "like"}
                            aria-label={t("book_card.like")}
                            className={`p-2 rounded-full border transition-all ${
                                sentiment === "like"
                                    ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                                    : "bg-transparent text-[var(--ink-muted)] border-[var(--border-strong)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                            }`}
                        >
                            <ThumbsUp size={14} strokeWidth={1.5} />
                        </button>
                        <button
                            type="button"
                            data-testid={`dislike-btn-${index}`}
                            onClick={() => reading.setSentiment({ ...book, recommendation_id: recommendationId }, "dislike")}
                            aria-pressed={sentiment === "dislike"}
                            aria-label={t("book_card.dislike")}
                            className={`p-2 rounded-full border transition-all ${
                                sentiment === "dislike"
                                    ? "bg-[var(--danger)] text-[var(--bg)] border-[var(--danger)]"
                                    : "bg-transparent text-[var(--ink-muted)] border-[var(--border-strong)] hover:border-[var(--danger)] hover:text-[var(--danger)]"
                            }`}
                        >
                            <ThumbsDown size={14} strokeWidth={1.5} />
                        </button>
                    </div>
                    <label
                        className="inline-flex items-center gap-2 cursor-pointer text-xs text-[var(--ink-muted)] select-none"
                        data-testid={`read-toggle-label-${index}`}
                    >
                        <input
                            type="checkbox"
                            data-testid={`read-toggle-${index}`}
                            checked={isRead}
                            onChange={() => reading.toggleRead(book)}
                            className="peer sr-only"
                        />
                        <span
                            className={`w-4 h-4 border flex items-center justify-center transition-colors ${
                                isRead
                                    ? "bg-[var(--accent)] border-[var(--accent)] text-[var(--bg)]"
                                    : "bg-transparent border-[var(--border-strong)]"
                            }`}
                        >
                            {isRead && <Check size={11} strokeWidth={2.5} />}
                        </span>
                        <span className="uppercase tracking-widest">{t("book_card.read_label")}</span>
                    </label>
                </div>
            )}

            <div className="mt-5 pt-1">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink-dim)] mb-2.5 font-medium">
                    {t("book_card.buy")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                    {stores.filter((s) => !s.indie).map((store) => {
                        const Icon = ICONS[store.icon] || ExternalLink;
                        const query = book.search_query || `${book.title} ${book.author}`;
                        return (
                            <a
                                key={store.id}
                                data-testid={`affiliate-${store.id}-${index}`}
                                href={store.build(query, book.isbn)}
                                target="_blank"
                                rel="noopener noreferrer sponsored"
                                onClick={() => {
                                    // Fire-and-forget click tracking; never blocks navigation.
                                    try {
                                        window.recomendalibros?.trackClick?.({
                                            store: store.id,
                                            book_title: book.title,
                                            book_author: book.author,
                                            isbn: book.isbn || null,
                                            recommendation_id: recommendationId || null,
                                        });
                                    } catch { /* ignore */ }
                                }}
                                className="flex items-center justify-center gap-1.5 border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded text-xs py-2 px-2 transition-all uppercase tracking-wider"
                            >
                                <Icon size={12} strokeWidth={1.5} />
                                <span>{store.name}</span>
                            </a>
                        );
                    })}
                    <UsedListingPill book={book} locale={i18n.language} testIdSuffix={`-${index}`} />
                </div>

                {/* Indie bookshop CTA — separated from the chains grid because it's
                    a mission-aligned link (not a paid affiliate) and visually distinct.
                    Renders only when `cegal` is among the available stores (Spain/PT). */}
                {stores.filter((s) => s.indie).map((store) => {
                    const Icon = ICONS[store.icon] || ExternalLink;
                    const query = book.search_query || `${book.title} ${book.author}`;
                    return (
                        <a
                            key={store.id}
                            data-testid={`affiliate-${store.id}-${index}`}
                            href={store.build(query, book.isbn)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                try {
                                    window.recomendalibros?.trackClick?.({
                                        store: store.id,
                                        book_title: book.title,
                                        book_author: book.author,
                                        isbn: book.isbn || null,
                                        recommendation_id: recommendationId || null,
                                    });
                                } catch { /* ignore */ }
                            }}
                            className="mt-2 flex items-center justify-center gap-1.5 border border-[var(--border-strong)] hover:border-[var(--ink)] hover:bg-[var(--bg-hover)] text-[var(--ink-muted)] hover:text-[var(--ink)] rounded text-xs py-2 px-2 transition-all uppercase tracking-wider"
                        >
                            <Icon size={12} strokeWidth={1.5} />
                            <span>{t("book_card.local_indie")}</span>
                        </a>
                    );
                })}

                <LocalBookshopCTA book={book} testIdSuffix={`-${index}`} />
            </div>
        </article>
    );
}


/**
 * Synopsis is hidden by default — click reveals it. Keeps cards short and
 * scannable, and lets users expand only the books they care about.
 *
 * NOTE: synopsis text is shipped with the recommendation payload (already
 * loaded), so expansion is instant. A future iteration can lazy-fetch via
 * a dedicated endpoint to shave LLM tokens off the initial response.
 */
function CollapsibleSynopsis({ text, index }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    return (
        <div className="mt-4">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                data-testid={`synopsis-toggle-${index}`}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] hover:text-[var(--accent)] transition-colors font-medium"
            >
                <span>{open ? t("book_card.hide_synopsis") : t("book_card.show_synopsis")}</span>
                <ChevronDown
                    size={12}
                    strokeWidth={1.8}
                    className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <p
                    className="text-sm text-[var(--ink-muted)] mt-2 leading-relaxed animate-fade-up"
                    data-testid={`synopsis-${index}`}
                >
                    {text}
                </p>
            )}
        </div>
    );
}
